import {useLoaderReset} from 'core/hooks/useLoaderReset';
import {useHistoryListener} from 'core/next/hooks/useHistotyListener';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {ProductOfferPrice} from 'models';
import {ProductPageContext} from 'modules/product-card/context';
import {countTotal, onListingHistoryPopState} from 'modules/product-card/helpers';
import {useProductDeliveryInfo} from 'modules/product-card/hooks/useProductDeliveryInfo';
import {useVisualProductImages} from 'modules/product-card/hooks/useVisualProductImages';
import {ProductPage} from 'modules/product-card/pages/ProductPage';
import {selectProductData, selectProductDeliveryInfo} from 'modules/product-card/selectors';
import {selectIsCurrentProductLoads} from 'modules/product-card/selectors';
import {actionSetCurrentProduct} from 'modules/product-card/slice';
import {actionSetProductSetList, actionSetProductSetTotalPrice} from 'modules/product-card/slice';
import {PRODUCT_TYPE} from 'modules/products/constants';
import {GALLERY_MODE} from 'modules/slider/constants';
import {TGalleryMode} from 'modules/slider/types';
import {ProductDetail} from 'new-models';
import Head from 'next/head';
import React, {FC, useEffect, useMemo} from 'react';

export const ProductPageProvider: FC = () => {
    useHistoryListener(onListingHistoryPopState);

    const dispatch = useAppDispatch();
    const productData = useAppSelector(selectProductData);
    const isProductLoading = useAppSelector(selectIsCurrentProductLoads);
    const productImages = useVisualProductImages(productData?.product);

    const productDeliveryInfo = useAppSelector(selectProductDeliveryInfo);

    const productIds = useMemo<ProductDetail['id'][] | null>(
        () => (productData?.product ? [productData.product.id] : null),
        [productData?.product]
    );

    useProductDeliveryInfo(productIds);

    useLoaderReset(actionSetCurrentProduct.type);

    useEffect(() => {
        if (productData?.productSet && productData.product?.price) {
            // TODO: for the time of mixing two mock servers
            dispatch(
                actionSetProductSetTotalPrice(
                    countTotal(productData.product.price as ProductOfferPrice, productData.productSet)
                )
            );
            dispatch(actionSetProductSetList(productData.productSet));
        }
    }, [dispatch, productData?.product?.price, productData?.productSet]);

    const galleryMode = useMemo<TGalleryMode>(() => {
        if (productData?.product?.type !== PRODUCT_TYPE.technical) {
            return GALLERY_MODE.basic;
        }

        return GALLERY_MODE.technical;
    }, [productData?.product?.type]);

    return (
        <>
            <Head>
                <title>{productData?.product.title}</title>
            </Head>
            <ProductPageContext.Provider
                value={{
                    galleryMode,
                    isProductLoading,
                    productData,
                    productDeliveryInfo,
                    productImages,
                }}
            >
                <ProductPage />
            </ProductPageContext.Provider>
        </>
    );
};
